import type { EditorContext } from "../tools/EditorContext";
import {
  moveConstructionItemsAt,
  movePiecesInList,
  pickAdjacentDecoration,
} from "../model/projectOps";
import {
  canJoinSelection,
  canSplitSelection,
  duplicateConstructionSelection,
  groupConstructionSelection,
  joinConstructionSelection,
  splitPolylineSelection,
  ungroupConstructionSelection,
} from "../model/constructionGroupActions";
import {
  duplicateChildFeatureSelection,
  duplicatePieceSelection,
} from "../model/pieceDuplicate";
import {
  descendantConstructionImageIds,
  descendantConstructionLineIds,
  groupAncestorIds,
  groupForTracingImage,
  isVisible,
  pieceForStitchLine,
  descendantConstructionGroupIds,
  pieceHardware,
  pieceStitchLines,
  projectConstructionGroups,
  projectConstructionLines,
  projectTracingImages,
  tracingImageBounds,
  type Hardware,
  type Hole,
  type Project,
  type StitchLine,
  type StitchShape,
  type TracingImage,
} from "../model/Project";
import { IMAGE_ICON_SVG } from "./icons";
import { stitchMatchPick } from "./stitchMatchPick";
import {
  matchForStitch,
  matchesForStitch,
  isStitchMatchMain,
  stitchMatchPartner,
} from "../model/stitchMatch";
import { stitchGridGroupFor } from "../model/constructionStitchGrid";
import { isFeatureEnabled } from "../featureFlags";
import {
  noteChipSpecs,
  notedTargets,
  partNoteChipSpecs,
  targetNoteChipSpecs,
} from "../model/connections";
import {
  isNoteVisible,
  moveNotesInList,
  noteRowLabel,
  numberedNotes,
} from "../model/notes";
import { noteIcon } from "../icons/noteIcon";
import {
  LayersDecorationRows,
  type LayersDecorationHost,
} from "./layersDecorationRows";
import {
  LayersConstructionRows,
  type LayersConstructionHost,
} from "./layersConstructionRows";
import {
  TRIANGLE_DOWN,
  TRIANGLE_RIGHT,
  COLLAPSE_ALL_GLYPH,
  EXPAND_ALL_GLYPH,
  bucketDecorations,
  makeGroupHeader,
  withRowIndent,
  makeRow,
} from "./layersRowKit";
import {
  deleteSelection,
  hasDeletableSelection,
} from "../model/deleteSelection";
import { findBrokenCoincidentConstraints } from "../model/constraintEnforcement";
import {
  CONNECTIONS,
  type ConnectionChipSpec,
  partTraceChipSpec,
  subTraceChipSpec,
} from "../model/connections";
import {
  stitchGridChipPartner,
  stitchGridSpecBase,
  stitchMatchSpecBase,
} from "../model/connections/stitchSpecs";
import {
  HARDWARE_COLOR_LABELS,
  HARDWARE_COLORS,
  HARDWARE_TYPE_LABELS,
} from "../hardware/HardwareCatalog";

import { layerChipsVisiblePref } from "../preferences";

export class LayersPanel
  implements LayersDecorationHost, LayersConstructionHost
{
  private root: HTMLElement;
  private ctx: EditorContext;
  /** The piece-decoration row builders (#879) — this panel OWNS it by
   *  composition and drives it from `render()`; it reaches back through the
   *  `LayersDecorationHost` methods below. */
  private decorationRows = new LayersDecorationRows(this);
  /** The construction-line row + group-tree + group-DnD builders (#883) —
   *  same composition pattern as `decorationRows`; the panel OWNS it and
   *  drives it from `render()`, and it reaches back through the
   *  `LayersConstructionHost` methods below. */
  private constructionRows = new LayersConstructionRows(this);
  /** Keys of currently-collapsed containers. Stored as `part:<id>` for parts
   *  and `group:<pieceId>:<bucketKey>` for stamp groups. State lives only as
   *  long as the panel instance — page reload starts everything expanded. */
  private collapsed = new Set<string>();
  /** The stitching scope revealed on the last render (#748) — serialized so
   *  a NEW scope selection expands its part group exactly once, and a later
   *  manual collapse isn't fought by re-renders. */
  private lastRevealedStitchingScope: string | null = null;
  /** Collapsible-container tree for the current render (#873): parent key →
   *  its direct child container keys. Rebuilt every `render()` from the
   *  headers as they append, so the expand/collapse-all buttons know each
   *  container's descendants. */
  private containerChildren = new Map<string, Set<string>>();
  /** Headers rendered this frame that carry an expand/collapse-all button,
   *  paired with their key so the post-render pass can set the glyph / hide
   *  the button (#873). Rebuilt every `render()`. */
  private expandAllHeaders: Array<{ key: string; el: HTMLElement }> = [];
  /** Pending "toggle deselect" timer for the part row. A real double-click
   *  cancels this so the dblclick handler (zoom-to-fit) wins; otherwise it
   *  fires after the dblclick window and deselects. */
  private deselectTimer: ReturnType<typeof setTimeout> | null = null;
  /** Show / hide the per-line constraint chip strips. Lives next to
   *  the "Parts" title as a small toggle so a busy panel can be
   *  decluttered without losing the constraint metadata. Persisted
   *  via PREF_CHIPS_VISIBLE_KEY. */
  private chipsVisible: boolean;
  /** Coincident-constraint ids whose refs no longer share a position
   *  (within `findBrokenCoincidentConstraints`'s default epsilon).
   *  Recomputed at the top of every `render()` from the committed
   *  model — drag-time positions are intentionally not consulted, so
   *  the chips reflect "is the saved relationship satisfied?" rather
   *  than flickering during a transient drag. The Scene's crosshair
   *  layer does its own live-position broken check (see
   *  `refreshConstructionCrosshairs`). */
  private brokenConstraintIds: ReadonlySet<string> = new Set();
  /** Payload of the in-flight construction drag (issue #80 layers-
   *  panel drag-and-drop). Set on `dragstart` from a draggable
   *  construction row, read on `dragover` / `drop` by candidate
   *  targets so they can validate the move before committing.
   *  Cleared on `dragend`. */
  private currentDragPayload: {
    lineIds: string[];
    groupIds: string[];
    imageIds: string[];
    sourceElement: HTMLElement;
  } | null = null;
  /** In-flight drag of a FLAT row list for reordering — leather parts (#678)
   *  and written notes (#157). Both are plain top-level lists with no nesting,
   *  so they share one interaction (and one set of drop-indicator classes);
   *  `kind` decides which model list the drop rewrites. Set on `dragstart`,
   *  cleared on `dragend` / `drop`. */
  private partDrag: {
    kind: "part" | "note";
    movedIds: string[];
    sourceElement: HTMLElement;
  } | null = null;

  /** The row attribute a flat-list drag anchors on, per kind. */
  private static readonly ROW_ID_ATTR = {
    part: "data-piece-id",
    note: "data-note-id",
  } as const;
  /** Inner container the dynamic list renders into. Lives inside
   *  `this.root` (`#layers-panel`) alongside the static `.layers-header`
   *  (issue #181) so the header isn't clobbered on every render.
   *  Falls back to `root` when the structure is missing — keeps
   *  unit tests / stand-alone uses from breaking. */
  private listEl: HTMLElement;
  /** A saved project is still loading (#1230): the empty list means "not
   *  here yet", not "nothing drawn". "quiet" says nothing (a load expected to
   *  be quick), "shown" says "Loading parts…", null is no load. */
  private loading: "quiet" | "shown" | null = null;
  /** Static header buttons (issue #181). The markup is owned by
   *  `index.html`; JS only attaches the click handlers and toggles
   *  the disabled / active state from `render()`. */
  private deleteSelectionBtn: HTMLButtonElement | null;
  private dupBtn: HTMLButtonElement | null;
  private groupBtn: HTMLButtonElement | null;
  private ungroupBtn: HTMLButtonElement | null;
  private splitBtn: HTMLButtonElement | null;
  private joinBtn: HTMLButtonElement | null;
  private chipToggleBtn: HTMLButtonElement | null;

  constructor(root: HTMLElement, ctx: EditorContext) {
    this.root = root;
    this.ctx = ctx;
    this.chipsVisible = layerChipsVisiblePref.get();
    this.listEl = root.querySelector<HTMLElement>(".layers-list") ?? root;
    this.installPartListDropZone();
    this.deleteSelectionBtn = root.querySelector<HTMLButtonElement>(
      '.layers-header-btn[data-action="delete-selection"]',
    );
    this.dupBtn = root.querySelector<HTMLButtonElement>(
      '.layers-header-btn[data-action="duplicate-construction"]',
    );
    this.groupBtn = root.querySelector<HTMLButtonElement>(
      '.layers-header-btn[data-action="group-construction"]',
    );
    this.ungroupBtn = root.querySelector<HTMLButtonElement>(
      '.layers-header-btn[data-action="ungroup-construction"]',
    );
    this.splitBtn = root.querySelector<HTMLButtonElement>(
      '.layers-header-btn[data-action="split-polyline"]',
    );
    this.joinBtn = root.querySelector<HTMLButtonElement>(
      '.layers-header-btn[data-action="join-construction"]',
    );
    this.chipToggleBtn = root.querySelector<HTMLButtonElement>(
      ".layers-chip-toggle",
    );
    this.deleteSelectionBtn?.addEventListener("click", (ev) => {
      ev.stopPropagation();
      this.deleteSelectedItems();
    });
    this.dupBtn?.addEventListener("click", (ev) => {
      ev.stopPropagation();
      this.duplicateSelection();
    });
    this.groupBtn?.addEventListener("click", (ev) => {
      ev.stopPropagation();
      groupConstructionSelection(this.ctx);
    });
    this.ungroupBtn?.addEventListener("click", (ev) => {
      ev.stopPropagation();
      ungroupConstructionSelection(this.ctx);
    });
    this.splitBtn?.addEventListener("click", (ev) => {
      ev.stopPropagation();
      splitPolylineSelection(this.ctx);
    });
    this.joinBtn?.addEventListener("click", (ev) => {
      ev.stopPropagation();
      joinConstructionSelection(this.ctx);
    });
    this.chipToggleBtn?.addEventListener("click", () => {
      this.chipsVisible = !this.chipsVisible;
      layerChipsVisiblePref.set(this.chipsVisible);
      this.render();
    });
    // Re-render when a stitch-match pick is armed / cancelled (#199) so the
    // candidate-seam affordance appears and clears.
    stitchMatchPick.subscribe(() => this.render());
    // Chip toggle's disabled state is driven by `render()` (issue
    // #181) — it only does something when there are construction
    // lines whose chips can be hidden / shown, so it stays disabled
    // on an empty project to match the duplicate / group / ungroup
    // buttons next to it.
    this.render();
  }

  /** Say whether a saved project is still on its way, and redraw. */
  setLoading(loading: "quiet" | "shown" | null) {
    if (this.loading === loading) return;
    this.loading = loading;
    this.refresh();
  }

  refresh() {
    this.render();
  }

  /** Trashcan in the Parts header — delete every selected item, whatever
   *  its kind (issue #474). Delegates to the shared `deleteSelection` so the
   *  button and the canvas Delete shortcut behave identically across parts,
   *  decorations, holes, cutouts, stitch lines, and construction lines /
   *  groups. No-op (and the button is disabled) when nothing is selected. */
  private deleteSelectedItems() {
    deleteSelection(this.ctx);
  }

  /** Duplicate button in the Parts header — clones whatever is selected, scoped
   *  to the selection's granularity (#907):
   *    - a single CHILD feature (stitch / cutout / hole / decoration) → duplicate
   *      just that feature into its owner part;
   *    - a whole leather part (piece) → duplicate the part with its children;
   *    - construction objects (lines / groups / images) → construction duplicate.
   *  Child scope is checked first because a child selection also carries its
   *  owner's `pieceId`; the three scopes are otherwise mutually exclusive. */
  private duplicateSelection() {
    if (duplicateChildFeatureSelection(this.ctx)) return;
    const sel = this.ctx.scene.getSelection();
    const hasPieceSelection =
      sel.pieceId != null || (sel.pieceIds?.length ?? 0) > 0;
    if (hasPieceSelection) {
      duplicatePieceSelection(this.ctx);
      return;
    }
    duplicateConstructionSelection(this.ctx);
  }

  // --- LayersDecorationHost --------------------------------------------
  // Injected services the extracted LayersDecorationRows (#879) reaches back
  // into. `toggle` / `buildStampIconHtml` / `buildClickToggle` /
  // `pathDataBounds` / the `set*Visibility` setters / `buildConnectionChip` /
  // `stitch*ChipSpec` / `installDragSource` are the panel's own members widened
  // to public; these accessors expose the list append target, the editor
  // context, the collapsed-set, the chip-visibility flag, and the deferred
  // deselect timer.
  getListEl(): HTMLElement {
    return this.listEl;
  }
  getCtx(): EditorContext {
    return this.ctx;
  }
  getCollapsed(): Set<string> {
    return this.collapsed;
  }
  getChipsVisible(): boolean {
    return this.chipsVisible;
  }
  getDeselectTimer(): ReturnType<typeof setTimeout> | null {
    return this.deselectTimer;
  }
  setDeselectTimer(v: ReturnType<typeof setTimeout> | null): void {
    this.deselectTimer = v;
  }

  // --- LayersConstructionHost ------------------------------------------
  // Extra injected services the extracted LayersConstructionRows (#883)
  // reaches back into on top of the shared decoration-host surface above:
  // the recomputed broken-constraint set (read by the per-line chip loop),
  // the in-flight construction drag payload (owned by `installDragSource`
  // here, read/cleared by the moved drop-target validation), and the
  // decoration-side tracing-image row (nested images render through it).
  getBrokenConstraintIds(): ReadonlySet<string> {
    return this.brokenConstraintIds;
  }
  getCurrentDragPayload(): {
    lineIds: string[];
    groupIds: string[];
    imageIds: string[];
    sourceElement: HTMLElement;
  } | null {
    return this.currentDragPayload;
  }
  setCurrentDragPayload(
    v: {
      lineIds: string[];
      groupIds: string[];
      imageIds: string[];
      sourceElement: HTMLElement;
    } | null,
  ): void {
    this.currentDragPayload = v;
  }
  appendTracingImageRow(
    image: TracingImage,
    indentPx: number,
    sel: {
      constructionId?: string | null;
      constructionIds?: string[];
      constructionGroupIds?: string[];
      tracingImageIds?: string[];
    },
    track: (row: { element: HTMLElement; matchesSelection: boolean }) => void,
  ): void {
    this.decorationRows.appendTracingImageRow(image, indentPx, sel, track);
  }

  public toggle(key: string) {
    if (this.collapsed.has(key)) this.collapsed.delete(key);
    else this.collapsed.add(key);
    this.render();
  }

  /** Record a collapsible container rendered this frame (#873): links `key`
   *  under `parentKey` in the container tree, and — when `headerEl` is given —
   *  remembers the header so `refreshExpandAllButtons` can set that row's
   *  expand/collapse-all glyph (or hide the button when it has no collapsible
   *  descendants). Called by every header builder as it appends its row; the
   *  tree is cleared at the top of each `render()`. */
  registerCollapsible(
    key: string,
    parentKey: string | null,
    headerEl: HTMLElement | null,
  ): void {
    if (!this.containerChildren.has(key)) {
      this.containerChildren.set(key, new Set());
    }
    if (parentKey) {
      let set = this.containerChildren.get(parentKey);
      if (!set) {
        set = new Set();
        this.containerChildren.set(parentKey, set);
      }
      set.add(key);
    }
    if (headerEl) this.expandAllHeaders.push({ key, el: headerEl });
  }

  /** Every collapsible container nested under `key` (transitive). Construction
   *  containers derive from the MODEL, not the rendered tree: a collapsed
   *  parent group hides its nested group headers, so the render tree would lose
   *  them and expand-all couldn't restore the subtree. Parts (and their
   *  decoration sub-groups, which never nest) use the render tree, which is
   *  always complete for them since a collapsed sub-group still renders its
   *  header. */
  private collectDescendantContainerKeys(key: string): Set<string> {
    if (key === "construction:project") {
      const project = this.ctx.history.state;
      return new Set(
        projectConstructionGroups(project).map(
          (g) => `construction-group:${g.id}`,
        ),
      );
    }
    if (key.startsWith("construction-group:")) {
      const groupId = key.slice("construction-group:".length);
      const project = this.ctx.history.state;
      // `descendantConstructionGroupIds` includes the group itself — drop it,
      // we only fold the groups NESTED under it.
      return new Set(
        descendantConstructionGroupIds(project, groupId)
          .filter((id) => id !== groupId)
          .map((id) => `construction-group:${id}`),
      );
    }
    const out = new Set<string>();
    const stack = [...(this.containerChildren.get(key) ?? [])];
    while (stack.length) {
      const k = stack.pop()!;
      if (out.has(k)) continue;
      out.add(k);
      for (const c of this.containerChildren.get(k) ?? []) stack.push(c);
    }
    return out;
  }

  /** Expand or collapse EVERY group nested under `key` in one action (#873).
   *  Collapses them all when any is currently open, else expands them all —
   *  so the one button flips the whole subtree either way. The container's own
   *  row is left as-is (it stays open so the now-collapsed children show). */
  public expandCollapseAll(key: string): void {
    const desc = this.collectDescendantContainerKeys(key);
    if (desc.size === 0) return;
    const anyOpen = [...desc].some((k) => !this.collapsed.has(k));
    for (const k of desc) {
      if (anyOpen) this.collapsed.add(k);
      else this.collapsed.delete(k);
    }
    this.render();
  }

  /** Post-render pass (#873): for each header carrying an expand/collapse-all
   *  button, set the glyph + label from the subtree's current state, or hide
   *  the button when the container turned out to have no collapsible
   *  descendants (e.g. a part with only single-item groups). Runs after the
   *  full list is built so the container tree is complete. */
  private refreshExpandAllButtons(): void {
    for (const { key, el } of this.expandAllHeaders) {
      const btn = el.querySelector<HTMLElement>(".collapse-all-btn");
      if (!btn) continue;
      const desc = this.collectDescendantContainerKeys(key);
      if (desc.size === 0) {
        btn.style.display = "none";
        continue;
      }
      btn.style.display = "";
      const allCollapsed = [...desc].every((k) => this.collapsed.has(k));
      btn.textContent = allCollapsed ? EXPAND_ALL_GLYPH : COLLAPSE_ALL_GLYPH;
      const label = allCollapsed ? "Expand all" : "Collapse all";
      btn.title = label;
      btn.setAttribute("aria-label", label);
    }
  }

  /** Build one chip element from a `ConnectionChipSpec`. Shared
   *  across every connection kind so the layers panel only owns
   *  ONE chip-rendering code path (issue #180 unification). The
   *  hover handler installs a peer-query against the shared
   *  `data-connection-key` attribute so a hover on EITHER side of
   *  any pair lights up every chip + Properties row in the same
   *  relationship. */
  public buildConnectionChip(spec: ConnectionChipSpec): HTMLSpanElement {
    const chip = document.createElement("span");
    chip.className = spec.className
      ? `constraint-chip ${spec.className}`
      : "constraint-chip";
    if (spec.unsatisfied) {
      chip.classList.add("unsatisfied");
      chip.dataset.unsatisfied = "true";
    }
    chip.title = spec.title;
    chip.setAttribute("aria-label", spec.ariaLabel);
    chip.dataset.connectionKind = spec.kind;
    chip.dataset.connectionKey = spec.connectionKey;
    chip.dataset.connectionRole = spec.role;
    if (spec.extraDatasetEntries) {
      for (const [key, value] of spec.extraDatasetEntries) {
        chip.dataset[key] = value;
      }
    }
    chip.innerHTML = spec.iconSvg;
    const connectionKey = spec.connectionKey;
    const hoverRefs = spec.hoverRefs;
    // Cross-highlight peers in BOTH the layers panel (sibling chips
    // on related lines) AND the Properties panel's Connections
    // section (issue #342). The reverse direction — hovering a
    // Properties row → lighting up matching layers-panel chips —
    // already queried `document`; the chip side was scoped to
    // `this.listEl` and so the rows-from-chip half went dark.
    chip.addEventListener("mouseenter", (ev) => {
      ev.stopPropagation();
      this.ctx.scene.setConstructionConstraintHover({ refs: hoverRefs });
      for (const peer of document.querySelectorAll<HTMLElement>(
        `[data-connection-key="${CSS.escape(connectionKey)}"]`,
      )) {
        peer.classList.add("related-hover");
      }
    });
    chip.addEventListener("mouseleave", (ev) => {
      ev.stopPropagation();
      this.ctx.scene.setConstructionConstraintHover(null);
      for (const peer of document.querySelectorAll<HTMLElement>(
        `[data-connection-key="${CSS.escape(connectionKey)}"]`,
      )) {
        peer.classList.remove("related-hover");
      }
    });
    // Chip click selects the OTHER side of the relationship and the
    // parts panel auto-scrolls it into view (#221). Destructive
    // actions still live only on the Properties row's × button, so a
    // stray click can't break the relationship. Specs without a
    // clickTarget keep the chip inert.
    const clickTarget = spec.clickTarget;
    if (clickTarget) {
      chip.classList.add("constraint-chip-clickable");
      chip.addEventListener("click", (ev) => {
        ev.stopPropagation();
        this.ctx.setSelection({
          pieceId: clickTarget.pieceId ?? null,
          decorationId: null,
          constructionId: clickTarget.constructionId ?? null,
          holeId: null,
          cutoutId: clickTarget.cutoutId ?? null,
          stitchId: clickTarget.stitchId ?? null,
          // Always written, never omitted: a chip that leads to a PART has to
          // clear a note selection, or the note's panel stays up over the part
          // the maker just asked to see.
          noteIds: clickTarget.noteId ? [clickTarget.noteId] : [],
        });
      });
    }
    return chip;
  }

  /** Chip spec for one member of a stitch match (#199), mirroring the
   *  Properties Connections row's spec (same `connectionKey` + directional text)
   *  so the two cross-highlight. */
  public stitchMatchChipSpec(
    match: ReturnType<typeof matchForStitch> & object,
    stitchId: string,
  ): ConnectionChipSpec {
    const isMain = isStitchMatchMain(match, stitchId);
    const partnerRef = stitchMatchPartner(match, stitchId);
    // One shared spec source with the Properties row (#984) — key, role, icon,
    // label, and the partner `clickTarget` can't drift apart again (#979).
    return stitchMatchSpecBase(
      match.id,
      partnerRef,
      this.stitchRefName(partnerRef),
      isMain,
    );
  }

  /** Chip spec marking a seam's place in the construction-stitch grid (#199
   *  Phase 3), or null when the seam isn't grid-aligned. Shares its
   *  `connectionKey` (keyed on the leader) with the Properties "Grid alignment"
   *  rows + the group's other chips so all cross-highlight together. */
  public stitchGridChipSpec(line: StitchLine): ConnectionChipSpec | null {
    // The leader carries `gridMain` (no `gridPhase`); followers carry `gridPhase`.
    if (line.gridPhase == null && line.gridMain !== true) return null;
    const group = stitchGridGroupFor(this.ctx.history.state, line.id);
    if (!group || group.members.length < 2) return null;
    const isLeader = line.gridMain === true;
    const leaderRef = group.leader;
    const key = leaderRef ? leaderRef.stitchId : line.id;
    const others = group.members.length - 1;
    const label = isLeader
      ? others === 1
        ? "Grid leader · 1 seam aligns to this"
        : `Grid leader · ${others} seams align to this`
      : `Grid follower · aligned to ${
          leaderRef ? this.stitchRefName(leaderRef) : "the grid leader"
        }`;
    // Click jumps to the grid partner (#221): followers go to the
    // leader; the leader goes to the first seam aligned to it.
    const clickRef = isLeader
      ? stitchGridChipPartner(group.members, line.id)
      : leaderRef;
    // Shared spec source with the Properties rows (#984); only the wording
    // differs (one summary chip vs one row per aligned seam).
    return stitchGridSpecBase(key, clickRef, isLeader, label);
  }

  /** Highest chip count on any single parts-list row (#994): the part row's
   *  trace chip, or a seam row's stitch-match + grid chips. Mirrors what those
   *  rows actually render — same builders (`partTraceChipSpec`,
   *  `matchesForStitch` + `stitchGridChipSpec`) behind the same feature gate —
   *  so the inline-vs-wrapped decision is measured against the real DOM.
   *  Callers check `chipsVisible` first, as the construction loop does. */
  private maxPartChips(project: Project): number {
    let max = 0;
    const matching = isFeatureEnabled("stitch-matching");
    for (const piece of project.pieces) {
      // Trace chip plus one chip per note pointing at this part (#157) — both
      // ride the part's own row, so they count together against the inline
      // layout threshold.
      const partChips =
        (partTraceChipSpec(project, piece) ? 1 : 0) +
        partNoteChipSpecs(project, piece.id).length;
      if (partChips > max) max = partChips;
      // Cutout and seam rows carry note chips of their own (#1076), and the
      // seam ones sit beside its match / grid chips — so both are measured
      // here, or a row with a note on it wraps where the layout said it
      // wouldn't.
      for (const cut of piece.cutouts) {
        const n =
          (subTraceChipSpec(project, {
            kind: "cutout",
            pieceId: piece.id,
            id: cut.id,
          })
            ? 1
            : 0) +
          targetNoteChipSpecs(project, {
            pieceId: piece.id,
            cutoutId: cut.id,
          }).length;
        if (n > max) max = n;
      }
      for (const line of pieceStitchLines(piece)) {
        const n =
          (subTraceChipSpec(project, {
            kind: "stitch",
            pieceId: piece.id,
            id: line.id,
          })
            ? 1
            : 0) +
          (matching
            ? matchesForStitch(project, line.id).length +
              (this.stitchGridChipSpec(line) ? 1 : 0)
            : 0) +
          targetNoteChipSpecs(project, {
            pieceId: piece.id,
            stitchId: line.id,
          }).length;
        if (n > max) max = n;
      }
    }
    return max;
  }

  /** Display name for a stitch line addressed by ref (matches the panel's
   *  "Stitch N" fallback). */
  private stitchRefName(ref: { pieceId: string; stitchId: string }): string {
    const piece = this.ctx.history.state.pieces.find(
      (p) => p.id === ref.pieceId,
    );
    if (!piece) return "another seam";
    const lines = pieceStitchLines(piece);
    const idx = lines.findIndex((l) => l.id === ref.stitchId);
    if (idx < 0) return "another seam";
    return lines[idx]!.name ?? `Stitch ${idx + 1}`;
  }

  private setPieceVisibility(pieceId: string, visible: boolean) {
    this.ctx.commit("Toggle visibility", (proj) => ({
      ...proj,
      pieces: proj.pieces.map((pc) =>
        pc.id === pieceId ? { ...pc, visible } : pc,
      ),
    }));
  }

  /** Show or hide every leather part at once — the Leather Parts section
   *  header's eye (#1109), the twin of the construction section's. */
  private setAllPiecesVisibility(visible: boolean) {
    this.ctx.commit(visible ? "Show all parts" : "Hide all parts", (proj) => ({
      ...proj,
      pieces: proj.pieces.map((pc) => ({ ...pc, visible })),
    }));
  }

  public setHolesVisibility(
    pieceId: string,
    ids: Set<string>,
    visible: boolean,
  ) {
    this.ctx.commit("Toggle visibility", (proj) => ({
      ...proj,
      pieces: proj.pieces.map((pc) =>
        pc.id !== pieceId
          ? pc
          : {
              ...pc,
              holes: pc.holes.map((h) =>
                ids.has(h.id) ? { ...h, visible } : h,
              ),
            },
      ),
    }));
  }

  public setHardwareVisibility(
    pieceId: string,
    ids: Set<string>,
    visible: boolean,
  ) {
    this.ctx.commit("Toggle visibility", (proj) => ({
      ...proj,
      pieces: proj.pieces.map((pc) =>
        pc.id !== pieceId
          ? pc
          : {
              ...pc,
              hardware: pieceHardware(pc).map((h) =>
                ids.has(h.id) ? { ...h, visible } : h,
              ),
            },
      ),
    }));
  }

  public setStitchVisibility(
    pieceId: string,
    ids: Set<string>,
    visible: boolean,
  ) {
    this.ctx.commit("Toggle visibility", (proj) => ({
      ...proj,
      pieces: proj.pieces.map((pc) =>
        pc.id !== pieceId
          ? pc
          : {
              ...pc,
              stitchLines: pieceStitchLines(pc).map((s) =>
                ids.has(s.id) ? { ...s, visible } : s,
              ),
            },
      ),
    }));
  }

  public setCutoutsVisibility(
    pieceId: string,
    ids: Set<string>,
    visible: boolean,
  ) {
    this.ctx.commit("Toggle visibility", (proj) => ({
      ...proj,
      pieces: proj.pieces.map((pc) =>
        pc.id !== pieceId
          ? pc
          : {
              ...pc,
              cutouts: pc.cutouts.map((c) =>
                ids.has(c.id) ? { ...c, visible } : c,
              ),
            },
      ),
    }));
  }

  private setDecorationsVisibility(
    pieceId: string,
    ids: Set<string>,
    visible: boolean,
  ) {
    this.ctx.commit("Toggle visibility", (proj) => ({
      ...proj,
      pieces: proj.pieces.map((pc) =>
        pc.id !== pieceId
          ? pc
          : {
              ...pc,
              decorations: pc.decorations.map((d) =>
                ids.has(d.id) ? { ...d, visible } : d,
              ),
            },
      ),
    }));
  }

  /** Universal "click toggles selection, double-click zooms-to-fit"
   *  behavior factory for layers-panel rows. The deselect on a
   *  second click is deferred so a real double-click can intercept
   *  before it fires (a dblclick is preceded by two single-click
   *  events). Pass `zoomBounds` to enable the dblclick zoom; omit
   *  it for rows that have nothing to fit-to. */
  public buildClickToggle(opts: {
    isSelected: boolean;
    select: () => void;
    deselect: () => void;
    zoomBounds?: () => {
      x: number;
      y: number;
      width: number;
      height: number;
    } | null;
  }): {
    onSelect: () => void;
    onDoubleClick?: () => void;
  } {
    const cancelTimer = () => {
      if (this.deselectTimer) {
        clearTimeout(this.deselectTimer);
        this.deselectTimer = null;
      }
    };
    const onSelect = () => {
      cancelTimer();
      if (opts.isSelected) {
        this.deselectTimer = setTimeout(() => {
          this.deselectTimer = null;
          opts.deselect();
        }, 250);
      } else {
        opts.select();
      }
    };
    const handlers: { onSelect: () => void; onDoubleClick?: () => void } = {
      onSelect,
    };
    if (opts.zoomBounds) {
      const getBounds = opts.zoomBounds;
      handlers.onDoubleClick = () => {
        cancelTimer();
        const b = getBounds();
        if (b && b.width > 0 && b.height > 0) {
          this.ctx.viewport.fitToBounds(b);
        }
      };
    }
    return handlers;
  }

  /** Bounds of a parsed pathData item — used by the dblclick-zoom for
   *  cutouts and construction lines that store geometry as SVG path
   *  data. Returns null on parse failure or empty bounds. */
  public pathDataBounds(pathData: string): {
    x: number;
    y: number;
    width: number;
    height: number;
  } | null {
    const scope = this.ctx.scene.getPaperScope();
    let item: paper.PathItem;
    try {
      item = new scope.CompoundPath({ pathData, insert: false });
    } catch {
      return null;
    }
    const b = item.bounds.clone();
    item.remove();
    if (b.width <= 0 || b.height <= 0) return null;
    return { x: b.x, y: b.y, width: b.width, height: b.height };
  }

  public buildStampIconHtml(stampId: string): string {
    const meta = this.ctx.library.get(stampId);
    if (!meta) return "";
    const start = meta.svg.indexOf("<svg");
    if (start < 0) return "";
    // Strip the file's width/height so CSS controls the rendered size in the
    // visibility cell. The viewBox handles aspect ratio.
    return meta.svg.slice(start).replace(/\s(width|height)="[^"]*"/g, "");
  }

  private render() {
    const project = this.ctx.history.state;
    const sel = this.ctx.scene.getSelection();
    // Reveal a freshly-selected stitching scope (#748): the "Stitch
    // settings" locator on a seam's panel selects its part's "Stitching"
    // row, which only renders while the part group is expanded — so a NEW
    // scope selection pops the part open. Keyed on the scope value so the
    // user can still collapse the part afterwards without the next render
    // forcing it back open.
    const scopeKey = sel.stitchingScope
      ? JSON.stringify(sel.stitchingScope)
      : null;
    if (scopeKey && scopeKey !== this.lastRevealedStitchingScope) {
      const scope = sel.stitchingScope!;
      if (scope.kind !== "project")
        this.collapsed.delete(`part:${scope.pieceId}`);
    }
    this.lastRevealedStitchingScope = scopeKey;
    // Re-evaluate which coincident constraints are currently
    // un-satisfied so the per-chip styling can flag them in red.
    // Cheap to recompute — runs once per render, not per chip.
    this.brokenConstraintIds = findBrokenCoincidentConstraints(
      project,
      projectConstructionLines(project),
      this.ctx.scene.getPaperScope(),
    );
    // Header buttons live in the static HTML (issue #181) — only
    // clear / re-render the list portion. Header button state is
    // updated below from the current selection.
    this.listEl.innerHTML = "";
    // Reset the per-render collapsible-container tree (#873) — repopulated as
    // headers append, consumed by `refreshExpandAllButtons` at the end.
    this.containerChildren.clear();
    this.expandAllHeaders = [];
    // Decide chip layout up-front so the first paint is in the
    // right mode. Chips render to the right of the label by
    // default; if any single construction line has more than 4
    // chips involving it (snap constraints + offset relationships
    // alike — both render as `.constraint-chip` in the trailing
    // strip), the row would overflow — fall back to the
    // wrap-below layout across all rows so the panel reads
    // consistently (issue #81).
    const CHIPS_INLINE_LIMIT = 4;
    let maxChips = 0;
    if (this.chipsVisible) {
      const allLines = projectConstructionLines(project);
      // Count chips across EVERY registered connection kind
      // (snap + offset + mirror today) so the inline-mode
      // threshold reflects the real per-row chip count, not just
      // a hand-picked subset. Pre-refactor this loop only counted
      // constraint + offset chips and missed mirror chips entirely
      // — a layout bug fixed by the unified iteration.
      for (const line of allLines) {
        let n = 0;
        for (const conn of CONNECTIONS) {
          n += conn.chipsForLine(line, project, {
            allLines,
            brokenIds: this.brokenConstraintIds,
          }).length;
        }
        if (n > maxChips) maxChips = n;
      }
      // Parts-list rows carry chips too (#994) — a part's trace chip, a seam's
      // stitch-match / grid chips. Measuring construction lines alone left
      // `maxChips` at 0 in any project with no chip-carrying construction
      // lines, so the `maxChips > 0` guard below never armed inline mode and
      // even a lone part chip wrapped onto its own indented line.
      maxChips = Math.max(maxChips, this.maxPartChips(project));
    }
    this.root.classList.toggle(
      "chips-inline",
      maxChips > 0 && maxChips <= CHIPS_INLINE_LIMIT,
    );
    // Parts header buttons are owned by the static HTML (issue #181);
    // here we only refresh their disabled / active state based on the
    // current selection.
    const hasConstructionLines = projectConstructionLines(project).length > 0;
    const selLineIds = sel.constructionIds ?? [];
    const selGroupIds = sel.constructionGroupIds ?? [];
    const selImageIds = sel.tracingImageIds ?? [];
    // Tracing images are construction objects too — a selection of
    // images alone counts, and Group / Duplicate operate on them.
    const hasSelection =
      selLineIds.length > 0 || selGroupIds.length > 0 || selImageIds.length > 0;
    // Leather parts are duplicatable too (#907) — a part selection (single or
    // multi) enables Duplicate even though Group / Split / Join stay
    // construction-only.
    const hasPieceSelection =
      sel.pieceId != null || (sel.pieceIds?.length ?? 0) > 0;
    // A single child feature (stitch / cutout / hole / decoration) is
    // duplicatable on its own (#907) — clones just that feature into its owner
    // part. Its owner's `pieceId` is set too, but call it out explicitly so the
    // gate reads as intentional.
    const hasChildFeatureSelection =
      sel.decorationId != null ||
      sel.holeId != null ||
      sel.cutoutId != null ||
      sel.stitchId != null ||
      (sel.stitchIds?.length ?? 0) > 0;
    const allGroups = projectConstructionGroups(project);
    const groupedLineIds = new Set<string>();
    const groupedImageIds = new Set<string>();
    for (const g of allGroups) {
      for (const id of g.lineIds) groupedLineIds.add(id);
      for (const id of g.imageIds ?? []) groupedImageIds.add(id);
    }
    const anyLineInGroup = selLineIds.some((id) => groupedLineIds.has(id));
    const anyImageInGroup = selImageIds.some((id) => groupedImageIds.has(id));
    const canUngroup =
      selGroupIds.length > 0 || anyLineInGroup || anyImageInGroup;
    if (this.deleteSelectionBtn) {
      // The trashcan deletes whatever is selected (issue #474) — parts,
      // decorations, holes, cutouts, stitch lines, or construction lines /
      // groups. Enabled whenever the selection holds something deletable,
      // using the same predicate the canvas Delete shortcut routes through
      // so the two never disagree.
      this.deleteSelectionBtn.disabled = !hasDeletableSelection(sel);
    }
    if (this.dupBtn) {
      // Duplicate works on any selected construction object (line, group, or
      // image), any leather part, AND any single child feature (#907), so gate
      // on any of those scopes holding something.
      this.dupBtn.disabled =
        !hasSelection && !hasPieceSelection && !hasChildFeatureSelection;
    }
    if (this.groupBtn) {
      this.groupBtn.disabled = !hasSelection;
    }
    if (this.ungroupBtn) {
      this.ungroupBtn.disabled = !canUngroup;
    }
    if (this.splitBtn) {
      // Split is line-only: groups aren't a meaningful target (each
      // child is still a separate line), and the action no-ops when
      // no selected line has ≥ 2 segments. `canSplitSelection`
      // parses pathData with paper to reject single-segment Lines /
      // primitive shapes — same per-line predicate the action uses.
      this.splitBtn.disabled =
        !hasConstructionLines ||
        selLineIds.length === 0 ||
        !canSplitSelection(this.ctx);
    }
    if (this.joinBtn) {
      // Join: at least two selected open construction lines whose
      // endpoints share a coincident constraint, forming a simple
      // chain (or closed cycle). `canJoinSelection` does the same
      // pre-check the action runs to decide whether the click would
      // do anything, so the button greys out when the user's
      // selection isn't actually joinable.
      this.joinBtn.disabled =
        !hasConstructionLines ||
        selLineIds.length < 2 ||
        !canJoinSelection(this.ctx);
    }
    if (this.chipToggleBtn) {
      this.chipToggleBtn.disabled = !hasConstructionLines;
      this.chipToggleBtn.classList.toggle("active", this.chipsVisible);
      this.chipToggleBtn.setAttribute(
        "aria-pressed",
        this.chipsVisible ? "true" : "false",
      );
      const label = this.chipsVisible
        ? "Hide constraint chips"
        : "Show constraint chips";
      this.chipToggleBtn.title = label;
      this.chipToggleBtn.setAttribute("aria-label", label);
    }

    let selectedRow: HTMLElement | null = null;

    // Written notes (#157) at the very top, mirroring the canvas: the list is
    // read top-of-stack first, and notes are drawn above everything else.
    const noteRow = this.appendNoteRows(project, sel);
    if (noteRow) selectedRow = noteRow;

    // Project-level "Stitching" row — bulk visibility + delete for all
    // stitch lines anywhere in the project. Selecting it opens the project
    // stitching defaults editor in PropertiesPanel.
    const projectStitchRow = this.appendProjectStitchingRow(project, sel);
    if (projectStitchRow) selectedRow = projectStitchRow;

    // Project-level construction lines — guides are project-scoped (not
    // attached to any part) and live above all parts in the layers list.
    // Render even when no leather parts exist so guides drawn on a bare
    // canvas show up in the panel.
    this.constructionRows.appendConstructionRows(project, sel, (row) => {
      if (row.matchesSelection) selectedRow = row.element;
    });

    // Tracing images (issue #489) — project-scoped reference photos.
    // Listed after the guides since they sit at the very bottom of the
    // canvas stack.
    this.appendTracingImageRows(project, sel, (row) => {
      if (row.matchesSelection) selectedRow = row.element;
    });

    // Empty state — only shown when there's truly nothing to list
    // (no parts, no project-level construction lines, and no tracing
    // images). The rows above already handle their own visibility.
    if (project.pieces.length === 0) {
      if (
        projectConstructionLines(project).length === 0 &&
        projectTracingImages(project).length === 0
      ) {
        if (this.loading !== "quiet") {
          const empty = document.createElement("div");
          empty.className = "empty";
          empty.textContent =
            this.loading === "shown"
              ? "Loading parts…"
              : "No parts yet — draw a shape to start.";
          this.listEl.appendChild(empty);
        }
      }
      // Still resolve the construction section's expand-all button (#873) —
      // this early-out fires whenever there are no leather parts, including
      // when project-level construction lines were rendered above.
      this.refreshExpandAllButtons();
      return;
    }

    // Leather parts section (#1109): the same top-level header the
    // construction lines get — collapse the whole list, and show / hide every
    // part at once from its leading icon. Shown for more than one part, like
    // the construction section; one part already has its own row to toggle.
    const showPartsSection = project.pieces.length > 1;
    const partsKey = "parts:project";
    let partsSectionKey: string | null = null;
    if (showPartsSection) {
      const partsOpen = !this.collapsed.has(partsKey);
      const anyVisible = project.pieces.some((pc) => isVisible(pc));
      const header = makeGroupHeader({
        toggleGlyph: partsOpen ? TRIANGLE_DOWN : TRIANGLE_RIGHT,
        onToggle: () => this.toggle(partsKey),
        onExpandCollapseAll: () => this.expandCollapseAll(partsKey),
        leadingHtml: "▣",
        hidden: !anyVisible,
        onLeadingClick: () => this.setAllPiecesVisibility(!anyVisible),
        label: `Leather Parts (${project.pieces.length})`,
        indentPx: 0,
      });
      header.setAttribute("data-parts-section", "project");
      this.listEl.appendChild(header);
      this.registerCollapsible(partsKey, null, header);
      partsSectionKey = partsKey;
      if (!partsOpen) {
        this.refreshExpandAllButtons();
        return;
      }
    }

    // Parts nest one level under their section header when it's shown.
    withRowIndent(showPartsSection ? 16 : 0, () => {
      // Reverse of the array so a HIGHER row draws on top; new parts append to the
      // array and so appear at the TOP of the list (#678).
      for (const piece of [...project.pieces].reverse()) {
        const partKey = `part:${piece.id}`;
        const partOpen = !this.collapsed.has(partKey);

        // The part row is "selected" when this part is in the multi-set
        // AND no sub-item of the lead piece is the actual focus. A
        // multi-piece selection clears sub-item ids during
        // normalization, so additional pieces always satisfy the
        // sub-item gate.
        const selectedPieceIds = sel.pieceIds ?? [];
        const partInMulti = selectedPieceIds.includes(piece.id);
        const partRowSelected =
          partInMulti &&
          !sel.decorationId &&
          !sel.holeId &&
          !sel.cutoutId &&
          !sel.stitchId &&
          !sel.constructionId &&
          !sel.stitchingScope;
        const partVisible = isVisible(piece);
        // Trace chip: a traced part shows one chip; hovering it cross-highlights
        // every source line's chip (and the lines on the canvas), and vice versa.
        let partTrailing: HTMLElement | undefined;
        if (this.chipsVisible) {
          const traceSpec = partTraceChipSpec(project, piece);
          // Plus one chip per note pointing here (#157) — which is the reverse
          // of the chip on the note's own row, and shares its key so hovering
          // either lights up both.
          const specs = [
            ...(traceSpec ? [traceSpec] : []),
            ...partNoteChipSpecs(project, piece.id),
          ];
          if (specs.length > 0) {
            partTrailing = document.createElement("span");
            partTrailing.className = "constraint-chip-strip";
            for (const spec of specs) {
              partTrailing.appendChild(this.buildConnectionChip(spec));
            }
          }
        }
        const row = makeRow({
          toggleGlyph: partOpen ? TRIANGLE_DOWN : TRIANGLE_RIGHT,
          onToggle: () => this.toggle(partKey),
          onExpandCollapseAll: () => this.expandCollapseAll(partKey),
          leadingGlyph: "▣",
          hidden: !partVisible,
          onLeadingClick: () => this.setPieceVisibility(piece.id, !partVisible),
          label: piece.name,
          trailing: partTrailing,
          selected: partRowSelected,
          onSelect: (ev) => {
            // Modifier semantics mirror the canvas SelectTool (issue
            // #251):
            //   - Shift  → additive: union the part into the set.
            //   - Cmd/Ctrl → toggle: flip the part in/out of the set.
            //   - plain  → replace: single-select this part. Clicking
            //     the already-single-selected row clears the selection
            //     (with the double-click defer below).
            if (this.deselectTimer) {
              clearTimeout(this.deselectTimer);
              this.deselectTimer = null;
            }
            const additive = ev.shiftKey;
            const toggle = ev.ctrlKey || ev.metaKey;
            if (toggle) {
              const next = partInMulti
                ? selectedPieceIds.filter((id) => id !== piece.id)
                : [...selectedPieceIds, piece.id];
              this.ctx.setSelection({
                pieceId: next[next.length - 1] ?? null,
                pieceIds: next,
                decorationId: null,
              });
              return;
            }
            if (additive) {
              if (partInMulti) return;
              const next = [...selectedPieceIds, piece.id];
              this.ctx.setSelection({
                pieceId: piece.id,
                pieceIds: next,
                decorationId: null,
              });
              return;
            }
            if (partRowSelected && selectedPieceIds.length === 1) {
              this.deselectTimer = setTimeout(() => {
                this.deselectTimer = null;
                this.ctx.setSelection({ pieceId: null, decorationId: null });
              }, 250);
            } else {
              this.ctx.setSelection({
                pieceId: piece.id,
                pieceIds: [piece.id],
                decorationId: null,
              });
            }
          },
          onDoubleClick: () => {
            if (this.deselectTimer) {
              clearTimeout(this.deselectTimer);
              this.deselectTimer = null;
            }
            const bounds = this.ctx.scene.getPieceBounds(piece.id);
            if (bounds) this.ctx.viewport.fitToBounds(bounds);
          },
          onDelete: () => {
            this.ctx.commit("Delete part", (proj) => ({
              ...proj,
              pieces: proj.pieces.filter((pc) => pc.id !== piece.id),
            }));
            if (sel.pieceId === piece.id) {
              this.ctx.setSelection({ pieceId: null, decorationId: null });
            }
          },
        });
        // Reorder handle (#678): drag the whole selection when this part is part
        // of it, else just this part.
        const partMovedIds =
          partInMulti && selectedPieceIds.length > 1
            ? selectedPieceIds
            : [piece.id];
        row.setAttribute("data-piece-id", piece.id);
        this.installPartDragSource(row, partMovedIds);
        this.listEl.appendChild(row);
        // Register the part as a collapsible container (#873) so its
        // expand/collapse-all button can reach the sub-groups appended below.
        this.registerCollapsible(partKey, partsSectionKey, row);
        if (partRowSelected) selectedRow = row;

        if (!partOpen) continue;

        this.decorationRows.appendHoleRows(piece, sel, (row) => {
          if (row.matchesSelection) selectedRow = row.element;
        });
        this.decorationRows.appendHardwareRows(piece, sel, (row) => {
          if (row.matchesSelection) selectedRow = row.element;
        });
        this.decorationRows.appendCutoutRows(piece, sel, (row) => {
          if (row.matchesSelection) selectedRow = row.element;
        });
        this.decorationRows.appendStitchRows(piece, sel, (row) => {
          if (row.matchesSelection) selectedRow = row.element;
        });

        const buckets = bucketDecorations(piece, this.ctx.library);
        for (const bucket of buckets) {
          if (bucket.decorations.length > 1) {
            const groupKey = `group:${piece.id}:${bucket.key}`;
            const groupOpen = !this.collapsed.has(groupKey);
            const bucketDecorationIds = new Set(
              bucket.decorations.map((d) => d.id),
            );
            const firstDec = bucket.decorations[0]!;
            const groupStampId =
              firstDec.kind === "stamp" ||
              firstDec.kind === "lineStamp" ||
              firstDec.kind === "fill"
                ? firstDec.stampId
                : null;
            const groupIconHtml = groupStampId
              ? this.buildStampIconHtml(groupStampId)
              : "";
            const groupAnyVisible = bucket.decorations.some((d) => d.visible);
            const headerRow = makeGroupHeader({
              toggleGlyph: groupOpen ? TRIANGLE_DOWN : TRIANGLE_RIGHT,
              onToggle: () => this.toggle(groupKey),
              leadingHtml: groupIconHtml || undefined,
              hidden: !groupAnyVisible,
              onLeadingClick: () =>
                this.setDecorationsVisibility(
                  piece.id,
                  bucketDecorationIds,
                  !groupAnyVisible,
                ),
              label: `${bucket.label} (${bucket.decorations.length})`,
              indentPx: 16,
              onDelete: () => {
                this.ctx.commit("Delete stamp group", (proj) => ({
                  ...proj,
                  pieces: proj.pieces.map((pc) =>
                    pc.id === piece.id
                      ? {
                          ...pc,
                          decorations: pc.decorations.filter(
                            (d) => !bucketDecorationIds.has(d.id),
                          ),
                        }
                      : pc,
                  ),
                }));
                // If the current selection was inside the deleted group, fall
                // back to the parent part so the keyboard stays consistent.
                if (
                  sel.pieceId === piece.id &&
                  sel.decorationId &&
                  bucketDecorationIds.has(sel.decorationId)
                ) {
                  this.ctx.setSelection({
                    pieceId: piece.id,
                    decorationId: null,
                  });
                }
                // Drop any group-collapsed flag — the group ceases to exist.
                this.collapsed.delete(groupKey);
              },
            });
            this.listEl.appendChild(headerRow);
            // A stamp group is a collapsible child of its part (#873).
            this.registerCollapsible(groupKey, partKey, null);
            if (!groupOpen) continue;
            for (const dec of bucket.decorations) {
              const drow = this.decorationRows.buildDecorationRow(
                piece,
                dec,
                sel,
                36,
              );
              this.listEl.appendChild(drow);
              if (sel.pieceId === piece.id && sel.decorationId === dec.id) {
                selectedRow = drow;
              }
            }
          } else {
            for (const dec of bucket.decorations) {
              const drow = this.decorationRows.buildDecorationRow(
                piece,
                dec,
                sel,
                16,
              );
              this.listEl.appendChild(drow);
              if (sel.pieceId === piece.id && sel.decorationId === dec.id) {
                selectedRow = drow;
              }
            }
          }
        }
      }
    });

    this.refreshExpandAllButtons();
    if (selectedRow) this.scrollRowIntoView(selectedRow);
  }

  /** Reveal the selected row inside the scrolling Parts panel (#868).
   *
   *  The panel (`this.root`, `#layers-panel`) is the scroll container and its
   *  `.layers-header` is `position: sticky; top: 0`, so the top slice of the
   *  scrollport is permanently occupied by the header. A plain
   *  `scrollIntoView({ block: "nearest" })` aligns an above-the-fold row to the
   *  container's top edge — i.e. *behind* the sticky header — leaving the freshly
   *  selected row invisible, which read as "scroll-to-selected doesn't work".
   *
   *  So scroll manually against the header-adjusted viewport: treat the usable
   *  region as everything below the header, and nudge only when the row falls
   *  outside it (out the top → align just under the header; out the bottom →
   *  align to the panel's bottom edge). A fully-visible row is left alone so an
   *  unrelated re-render never yanks the panel around. */
  private scrollRowIntoView(row: HTMLElement) {
    const container = this.root;
    // jsdom returns zeroed rects and doesn't implement scrolling; bail so unit
    // tests exercising render() don't depend on layout that doesn't exist.
    if (typeof container.scrollTop !== "number") return;
    const cRect = container.getBoundingClientRect();
    const rRect = row.getBoundingClientRect();
    // Sticky header eats the top of the scrollport — keep clear of it.
    const header = container.querySelector<HTMLElement>(".layers-header");
    const headerH = header ? header.getBoundingClientRect().height : 0;
    const viewTop = cRect.top + headerH;
    const viewBottom = cRect.bottom;
    if (rRect.top < viewTop) {
      // Row is above the fold (or hidden behind the header): scroll up so its
      // top clears the header.
      container.scrollTop -= viewTop - rRect.top;
    } else if (rRect.bottom > viewBottom) {
      // Row is below the fold: scroll down until its bottom reaches the edge.
      container.scrollTop += rRect.bottom - viewBottom;
    }
    // Otherwise the row is already fully visible — leave the scroll position be.
  }

  /** Top-level "Stitching" entry that bulk-controls every stitch line in
   *  every part. Visible only when the project has at least one stitch line
   *  — there's nothing to act on otherwise. Returns the rendered row if
   *  it's the active selection (so the caller can scroll to it). */
  /**
   * The project's written notes (#157), under one collapsible heading.
   *
   * Listed flat and numbered rather than nested under the part each points at:
   * the number IS the note's name — it is what the badge on the canvas and the
   * line on the printed notes page both show — so a maker reading "3" on the
   * drawing can find "3" here without first working out which part it belongs
   * to. Which part it points at is on the row as a tooltip, and in full on the
   * note's own panel.
   *
   * Returns the selected row, if one of these is it, so the panel can scroll it
   * into view like every other section does.
   */
  private appendNoteRows(
    project: Project,
    sel: ReturnType<typeof this.ctx.scene.getSelection>,
  ): HTMLElement | null {
    const notes = numberedNotes(project);
    if (notes.length === 0) return null;
    const key = "notes";
    const open = !this.collapsed.has(key);
    const anyVisible = notes.some(({ note }) => isNoteVisible(note));
    const selectedIds = new Set(sel.noteIds ?? []);

    const header = makeGroupHeader({
      toggleGlyph: open ? TRIANGLE_DOWN : TRIANGLE_RIGHT,
      onToggle: () => this.toggle(key),
      leadingHtml: noteIcon,
      hidden: !anyVisible,
      selected: false,
      label: `Notes (${notes.length})`,
      indentPx: 0,
      // Bulk hide / show. Per-note, and not the canvas-wide Notes button:
      // that one is a viewing toggle that lives outside the project, so it
      // would not survive a reload or reach the printed page.
      onLeadingClick: () => {
        this.ctx.commit("Toggle note visibility", (proj) => ({
          ...proj,
          notes: (proj.notes ?? []).map((n) => ({
            ...n,
            visible: !anyVisible,
          })),
        }));
      },
      onDelete: () => {
        this.ctx.commit("Delete all notes", (proj) => ({
          ...proj,
          notes: [],
        }));
        this.ctx.setSelection({
          pieceId: sel.pieceId,
          decorationId: null,
          noteIds: [],
        });
      },
    });
    this.listEl.appendChild(header);
    this.registerCollapsible(key, null, header);
    if (!open) return null;

    let selectedRow: HTMLElement | null = null;
    for (const { note, number } of notes) {
      const visible = isNoteVisible(note);
      const isSelected = selectedIds.has(note.id);
      const targets = notedTargets(project, note);
      // The "points at" link gets the same chip every other relationship in
      // this list gets (#157): hover cross-highlights the part's matching chip,
      // click jumps to the part.
      let noteTrailing: HTMLElement | undefined;
      if (this.chipsVisible) {
        const specs = noteChipSpecs(project, note);
        if (specs.length > 0) {
          noteTrailing = document.createElement("span");
          noteTrailing.className = "constraint-chip-strip";
          for (const spec of specs) {
            noteTrailing.appendChild(this.buildConnectionChip(spec));
          }
        }
      }
      const row = makeRow({
        leadingGlyph: "●",
        hidden: !visible,
        trailing: noteTrailing,
        onLeadingClick: () => {
          this.ctx.commit("Toggle note visibility", (proj) => ({
            ...proj,
            notes: (proj.notes ?? []).map((n) =>
              n.id === note.id ? { ...n, visible: !visible } : n,
            ),
          }));
        },
        label: `${number}. ${noteRowLabel(note.text)}`,
        selected: isSelected,
        indentPx: 16,
        dataAttributes: { "data-note-id": note.id },
        onSelect: () =>
          this.ctx.setSelection({
            pieceId: null,
            decorationId: null,
            noteIds: [note.id],
          }),
        onDelete: () => {
          this.ctx.commit("Delete note", (proj) => ({
            ...proj,
            notes: (proj.notes ?? []).filter((n) => n.id !== note.id),
          }));
          if (isSelected) {
            this.ctx.setSelection({
              pieceId: null,
              decorationId: null,
              noteIds: [],
            });
          }
        },
      });
      row.title =
        targets.length > 0
          ? `Points at ${targets.map((t) => t.label).join(", ")}`
          : "Not attached to anything";
      // Draggable for reordering, exactly like a part row — and reordering is
      // renumbering, since a note's number is its place in this list.
      this.installFlatRowDragSource(row, "note", [note.id]);
      this.listEl.appendChild(row);
      if (isSelected) selectedRow = row;
    }
    return selectedRow;
  }

  private appendProjectStitchingRow(
    project: Project,
    sel: ReturnType<typeof this.ctx.scene.getSelection>,
  ): HTMLElement | null {
    const allLines = project.pieces.flatMap((p) => pieceStitchLines(p));
    if (allLines.length === 0) return null;
    const allIds = new Set(allLines.map((l) => l.id));
    const anyVisible = allLines.some((l) => isVisible(l));
    const isSelected = sel.stitchingScope?.kind === "project";

    const header = makeGroupHeader({
      toggleGlyph: "",
      onToggle: () => {},
      leadingHtml: "⫶",
      hidden: !anyVisible,
      selected: isSelected,
      label: `Stitching — project (${allLines.length})`,
      indentPx: 0,
      onLeadingClick: () => {
        // Bulk visibility flip for every stitch line in the project.
        this.ctx.commit("Toggle stitching visibility", (proj) => ({
          ...proj,
          pieces: proj.pieces.map((pc) => ({
            ...pc,
            stitchLines: pieceStitchLines(pc).map((l) =>
              allIds.has(l.id) ? { ...l, visible: !anyVisible } : l,
            ),
          })),
        }));
      },
      onSelect: () =>
        this.ctx.setSelection({
          pieceId: null,
          decorationId: null,
          stitchingScope: { kind: "project" },
        }),
      onDelete: () => {
        this.ctx.commit("Delete all stitching", (proj) => ({
          ...proj,
          pieces: proj.pieces.map((pc) => ({
            ...pc,
            stitchLines: pieceStitchLines(pc).filter((l) => !allIds.has(l.id)),
          })),
        }));
        if (sel.stitchId && allIds.has(sel.stitchId)) {
          this.ctx.setSelection({
            pieceId: sel.pieceId,
            decorationId: null,
            stitchId: null,
          });
        }
      },
    });
    this.listEl.appendChild(header);
    return isSelected ? header : null;
  }

  /** Top-level (ungrouped) tracing images (issue #489) — reference
   *  photos imported through the trace-an-image dialog. Images that
   *  belong to a construction group (issue #509) render nested inside
   *  their group via `appendConstructionGroupTree` instead, so this
   *  pass skips them. Each row is draggable so it can be dropped into
   *  a group; reorder strips let it be repositioned among its peers. */
  private appendTracingImageRows(
    project: Project,
    sel: ReturnType<typeof this.ctx.scene.getSelection>,
    track: (row: { element: HTMLElement; matchesSelection: boolean }) => void,
  ) {
    for (const image of projectTracingImages(project)) {
      if (groupForTracingImage(project, image.id)) continue;
      this.appendDropStrip(null, { kind: "image", id: image.id }, 0);
      this.decorationRows.appendTracingImageRow(image, 0, sel, track);
    }
  }

  /** Builds the drag payload for a construction row. Follows the
   *  Finder convention: if the dragged row is part of the current
   *  selection, every selected item of the **same kind** comes along;
   *  otherwise just the dragged row moves. We deliberately decouple
   *  the two selection axes — dragging a line never picks up
   *  `constructionGroupIds` and vice versa — because Cmd+G leaves
   *  both populated and the user grabbing a line afterwards expects
   *  to move the line, not the enclosing group.
   *
   *  Final pass: when the payload includes nested groups, the
   *  child entries (descendant lines, descendant groups) are
   *  stripped — they ride along inside their ancestor and listing
   *  them explicitly would promote them past it. */
  private buildDragPayload(source: {
    kind: "line" | "group" | "image";
    id: string;
  }): { lineIds: string[]; groupIds: string[]; imageIds: string[] } {
    const project = this.ctx.history.state;
    const sel = this.ctx.scene.getSelection();
    const selLines = sel.constructionIds ?? [];
    const selGroups = sel.constructionGroupIds ?? [];
    const selImages = sel.tracingImageIds ?? [];
    let lineIds: string[] = [];
    let groupIds: string[] = [];
    let imageIds: string[] = [];
    if (source.kind === "line") {
      lineIds =
        selLines.includes(source.id) && selLines.length > 1
          ? [...selLines]
          : [source.id];
    } else if (source.kind === "image") {
      imageIds =
        selImages.includes(source.id) && selImages.length > 1
          ? [...selImages]
          : [source.id];
    } else {
      groupIds =
        selGroups.includes(source.id) && selGroups.length > 1
          ? [...selGroups]
          : [source.id];
    }
    if (groupIds.length > 0) {
      const movingGroupSet = new Set(groupIds);
      const descendants = new Set<string>();
      const imageDescendants = new Set<string>();
      for (const gid of groupIds) {
        for (const lid of descendantConstructionLineIds(project, gid)) {
          descendants.add(lid);
        }
        for (const iid of descendantConstructionImageIds(project, gid)) {
          imageDescendants.add(iid);
        }
      }
      lineIds = lineIds.filter((id) => !descendants.has(id));
      imageIds = imageIds.filter((id) => !imageDescendants.has(id));
      groupIds = groupIds.filter((gid) => {
        const ancestors = groupAncestorIds(project, gid);
        return !ancestors.some((aid) => movingGroupSet.has(aid));
      });
    }
    return { lineIds, groupIds, imageIds };
  }

  /** Wires a layers-panel row as a drag source for construction-line /
   *  construction-group re-parenting. Captures the payload on
   *  `dragstart`, paints a `drag-source` faded state on the row, and
   *  clears both on `dragend`. Sets a placeholder string on
   *  `dataTransfer` because Firefox refuses to fire `dragstart` /
   *  `dragend` unless `setData` is called — we use the in-memory
   *  payload, but the DataTransfer hand-off has to be non-empty. */
  public installDragSource(
    el: HTMLElement,
    source: { kind: "line" | "group" | "image"; id: string },
  ): void {
    el.setAttribute("draggable", "true");
    el.addEventListener("dragstart", (ev) => {
      const payload = this.buildDragPayload(source);
      if (
        payload.lineIds.length === 0 &&
        payload.groupIds.length === 0 &&
        payload.imageIds.length === 0
      ) {
        ev.preventDefault();
        return;
      }
      this.currentDragPayload = {
        lineIds: payload.lineIds,
        groupIds: payload.groupIds,
        imageIds: payload.imageIds,
        sourceElement: el,
      };
      if (ev.dataTransfer) {
        ev.dataTransfer.effectAllowed = "move";
        ev.dataTransfer.setData("text/plain", "construction-items");
      }
      el.classList.add("drag-source");
    });
    el.addEventListener("dragend", () => {
      el.classList.remove("drag-source");
      for (const t of this.listEl.querySelectorAll<HTMLElement>(
        ".drop-target-active",
      )) {
        t.classList.remove("drop-target-active");
      }
      this.currentDragPayload = null;
    });
  }

  /** Makes a leather PART row draggable for list reordering (#678). Drags the
   *  whole current selection when the row is part of it, else just this part. */
  private installPartDragSource(el: HTMLElement, movedIds: string[]): void {
    this.installFlatRowDragSource(el, "part", movedIds);
  }

  /** Shared by the two flat lists that reorder by dragging a row: leather
   *  parts (#678) and notes (#157). */
  private installFlatRowDragSource(
    el: HTMLElement,
    kind: "part" | "note",
    movedIds: string[],
  ): void {
    el.setAttribute("draggable", "true");
    el.addEventListener("dragstart", (ev) => {
      this.partDrag = { kind, movedIds, sourceElement: el };
      if (ev.dataTransfer) {
        ev.dataTransfer.effectAllowed = "move";
        // Firefox needs a non-empty payload to fire drag events at all.
        ev.dataTransfer.setData("text/plain", `leather-${kind}`);
      }
      el.classList.add("drag-source");
    });
    el.addEventListener("dragend", () => {
      el.classList.remove("drag-source");
      this.clearPartDropIndicator();
      this.partDrag = null;
    });
  }

  /** One continuous drop zone over the whole list (#678): during a part drag,
   *  ANY cursor position resolves to an insertion boundary (snapped to the
   *  nearest row's top/bottom half) — there are no dead zones between rows.
   *  Attached once; it only acts while a part drag is in flight. */
  private installPartListDropZone(): void {
    const el = this.listEl;
    el.addEventListener("dragover", (ev) => {
      if (!this.partDrag) return;
      const before = this.computePartDropBefore(ev.clientY);
      if (before === undefined) return; // no parts to reorder
      ev.preventDefault();
      if (ev.dataTransfer) ev.dataTransfer.dropEffect = "move";
      this.showPartDropIndicator(before);
    });
    el.addEventListener("dragleave", (ev) => {
      if (!this.partDrag) return;
      const next = (ev as DragEvent).relatedTarget as Node | null;
      if (next && el.contains(next)) return;
      this.clearPartDropIndicator();
    });
    el.addEventListener("drop", (ev) => {
      if (!this.partDrag) return;
      const drag = this.partDrag;
      const before = this.computePartDropBefore(ev.clientY);
      this.clearPartDropIndicator();
      this.partDrag = null;
      if (before === undefined) return;
      ev.preventDefault();
      ev.stopPropagation();
      if (drag.kind === "note") {
        // Numbering follows list order — `numberedNotes` counts down the
        // array — so moving a row here renumbers the badge on the drawing and
        // the line on the printed page with it. That IS the feature: the
        // maker orders the notes the way the work is done.
        this.ctx.commit("Reorder notes", (proj) =>
          moveNotesInList(proj, drag.movedIds, before),
        );
        this.ctx.setSelection({
          pieceId: null,
          decorationId: null,
          noteIds: drag.movedIds,
        });
        return;
      }
      this.ctx.commit("Reorder parts", (proj) =>
        movePiecesInList(proj, drag.movedIds, before),
      );
      this.ctx.setSelection({
        pieceId: drag.movedIds[drag.movedIds.length - 1] ?? null,
        pieceIds: drag.movedIds,
        decorationId: null,
      });
    });
  }

  /** The list-position anchor a drop at `clientY` targets: the id of the part to
   *  insert BEFORE, or null for the bottom of the list. Only NON-dragged rows are
   *  anchors, so hovering the dragged block itself resolves to the boundary just
   *  past it (a clean no-op) rather than an ambiguous "before myself". Returns
   *  undefined when there are no parts. */
  private computePartDropBefore(clientY: number): string | null | undefined {
    const moved = new Set(this.partDrag?.movedIds ?? []);
    const attr = this.draggedRowAttr();
    const rows = [...this.listEl.querySelectorAll<HTMLElement>(`[${attr}]`)];
    if (rows.length === 0) return undefined;
    for (const row of rows) {
      const id = row.getAttribute(attr) ?? "";
      if (moved.has(id)) continue; // skip the dragged rows as anchors
      const r = row.getBoundingClientRect();
      if (clientY < r.top + r.height / 2) return id;
    }
    return null; // below every anchor's midpoint → bottom of the list
  }

  /** Paints the insertion line: a bar at the TOP of the `beforeId` row, or the
   *  BOTTOM of the last row when dropping at the end (null). */
  private showPartDropIndicator(beforeId: string | null): void {
    this.clearPartDropIndicator();
    const attr = this.draggedRowAttr();
    const rows = [...this.listEl.querySelectorAll<HTMLElement>(`[${attr}]`)];
    if (rows.length === 0) return;
    if (beforeId === null) {
      rows[rows.length - 1]!.classList.add("part-drop-after");
    } else {
      rows
        .find((r) => r.getAttribute(attr) === beforeId)
        ?.classList.add("part-drop-before");
    }
  }

  /** Which rows the in-flight flat-list drag can drop between. Parts by
   *  default, so a stray dragover with nothing in flight behaves as before. */
  private draggedRowAttr(): string {
    return LayersPanel.ROW_ID_ATTR[this.partDrag?.kind ?? "part"];
  }

  private clearPartDropIndicator(): void {
    for (const r of this.listEl.querySelectorAll<HTMLElement>(
      ".part-drop-before, .part-drop-after",
    )) {
      r.classList.remove("part-drop-before", "part-drop-after");
    }
  }

  /** Drop-strip variant of `installDropTarget` — wires a thin zone
   *  between two rows (or at the trailing end of a parent's children)
   *  as a position-aware drop target. The cursor (`insertBefore`)
   *  feeds straight into `moveConstructionItemsAt` so the moving
   *  items splice in immediately before that anchor in the matching
   *  sibling array. `insertBefore: null` appends at the end of the
   *  target parent.
   *
   *  Mirrors the existing `installDropTarget` callback structure so
   *  the two flows feel identical to the user: hover lights the
   *  strip via `drop-target-active`, leave clears it, drop commits.
   *  Cycle-prevention + cursor-in-moving-set checks live in
   *  `canReorderInto`, called from both `dragover` and `drop` so the
   *  highlight is only shown when the drop would actually do
   *  something. */
  private installReorderDropTarget(
    el: HTMLElement,
    parentGroupId: string | null,
    insertBefore: { kind: "line" | "group" | "image"; id: string } | null,
  ): void {
    el.addEventListener("dragover", (ev) => {
      if (!this.canReorderInto(parentGroupId, insertBefore)) return;
      ev.preventDefault();
      if (ev.dataTransfer) ev.dataTransfer.dropEffect = "move";
      el.classList.add("drop-target-active");
    });
    el.addEventListener("dragleave", (ev) => {
      const next = (ev as DragEvent).relatedTarget as Node | null;
      if (next && el.contains(next)) return;
      el.classList.remove("drop-target-active");
    });
    el.addEventListener("drop", (ev) => {
      el.classList.remove("drop-target-active");
      const payload = this.currentDragPayload;
      this.currentDragPayload = null;
      if (!payload) return;
      if (!this.canReorderInto(parentGroupId, insertBefore, payload)) return;
      ev.preventDefault();
      ev.stopPropagation();
      this.ctx.commit("Move construction items", (proj) =>
        moveConstructionItemsAt(
          proj,
          {
            lineIds: payload.lineIds,
            groupIds: payload.groupIds,
            imageIds: payload.imageIds,
          },
          parentGroupId,
          insertBefore,
        ),
      );
      this.ctx.setSelection({
        pieceId: null,
        decorationId: null,
        constructionIds: payload.lineIds,
        constructionGroupIds: payload.groupIds,
        tracingImageIds: payload.imageIds,
      });
    });
  }

  /** Validity gate for a reorder-strip drop: reject self-anchored
   *  drops (cursor in the moving set) and group cycles (target
   *  parent is a descendant of any moving group). Pure-reorder
   *  no-ops aren't filtered here — the model treats them as
   *  idempotent and we'd rather light up the strip than miss a
   *  legitimate move. */
  private canReorderInto(
    parentGroupId: string | null,
    insertBefore: { kind: "line" | "group" | "image"; id: string } | null,
    payload: {
      lineIds: string[];
      groupIds: string[];
      imageIds: string[];
    } | null = this.currentDragPayload,
  ): boolean {
    if (!payload) return false;
    if (insertBefore) {
      if (
        insertBefore.kind === "line" &&
        payload.lineIds.includes(insertBefore.id)
      )
        return false;
      if (
        insertBefore.kind === "group" &&
        payload.groupIds.includes(insertBefore.id)
      )
        return false;
      if (
        insertBefore.kind === "image" &&
        payload.imageIds.includes(insertBefore.id)
      )
        return false;
    }
    if (parentGroupId !== null) {
      const ancestors = new Set([
        ...groupAncestorIds(this.ctx.history.state, parentGroupId),
        parentGroupId,
      ]);
      for (const gid of payload.groupIds) {
        if (ancestors.has(gid)) return false;
      }
    }
    return true;
  }

  /** Emits one thin drop-strip row anchored at `(parentGroupId,
   *  insertBefore)` and appends it to the flat panel root. The strip
   *  is normally a tiny invisible spacer; on `drop-target-active` it
   *  reveals a horizontal yellow insertion indicator (see the
   *  `.layers-drop-strip` rules in styles.css). The strip is
   *  drag-source-free — it's a pure landing zone, not draggable
   *  itself. Indent matches the parent's children indent so the
   *  indicator visually sits inside the right nesting level. */
  public appendDropStrip(
    parentGroupId: string | null,
    insertBefore: { kind: "line" | "group" | "image"; id: string } | null,
    indentPx: number,
  ): void {
    const strip = document.createElement("div");
    strip.className = "layers-drop-strip";
    strip.style.setProperty("--strip-indent", `${indentPx}px`);
    strip.setAttribute("data-drop-parent", parentGroupId ?? "root");
    strip.setAttribute("data-drop-before-kind", insertBefore?.kind ?? "end");
    if (insertBefore) {
      strip.setAttribute("data-drop-before-id", insertBefore.id);
    }
    this.installReorderDropTarget(strip, parentGroupId, insertBefore);
    this.listEl.appendChild(strip);
  }
}
